import React, { useState } from "react";
import styled from "styled-components";
import { Tabs as MuiTabs } from "@material-ui/core";
import { Tab as MuiTab } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { Flex } from "rebass";

import Extraction from "./extraction";
import {
  selectGeneralExtractions,
  selectPaymentExtractions,
  selectLineItemExtractions,
  selectCustomerExtractions,
  selectVendorExtractions
} from "../../store/selector";
import { addLine, setActiveBoxes, removeLines } from "../../store/action";
import Pagination from "../pagination";

const Section = styled.div`
  padding: 0 20px;

  @media (min-width: 1024px) {
    width: 50%;
    padding: 0 0 0 40px;
  }
`;

const StyledTabs = styled(MuiTabs)`
  .MuiTabs-indicator {
    background-color: ${(props) => props.theme.palette.primary};
  }
`;

const StyledTab = styled(MuiTab)`
  font-family: ${(props) => props.theme.typo.fontFamily};
  min-width: 60px;

  @media (min-width: 768px) {
    min-width: 80px;
  }

  &.MuiTab-textColorInherit {
    color: ${(props) => props.theme.palette.primary};
    font-size: 10px;

    @media (min-width: 768px) {
      font-size: 14px;
    }
  }
`;

const TabHeader = styled.div`
  display: none;
`;

const LineItemGroup = styled.div`
  border-bottom: 3px solid #eeeeee;
  display: ${(props) => (props.isVisible ? "block" : "none")};
`;

const TabContent = ({ children }) => {
  return <div>{children}</div>;
};

const CustomTabs = () => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);
  const extractionsGeneral = useSelector(selectGeneralExtractions);
  const extractionsCustomer = useSelector(selectCustomerExtractions);
  const extractionsVendor = useSelector(selectVendorExtractions);
  const extractionsPayment = useSelector(selectPaymentExtractions);
  const extractionsItems = useSelector(selectLineItemExtractions);
  const [currentLineItem, setCurrentLineItem] = useState(0);

  const handleChange = (event, newValue) => setValue(newValue);

  const handleMouseOver = (e, bbox_ids) => {
    const target = e.currentTarget;
    const { x, y, height } = target.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    bbox_ids.forEach((id) => {
      const el = document.getElementById(`box-${id}`);

      if (el) {
        const boxCoords = el.getBoundingClientRect();

        dispatch(
          addLine({
            x1: x,
            y1: y + scrollTop + height / 2,
            x2: boxCoords.x + boxCoords.width,
            y2: boxCoords.y + scrollTop + boxCoords.height / 2
          })
        );
      }
    });

    dispatch(setActiveBoxes(bbox_ids));
  };

  const handleMouseOut = () => {
    dispatch(removeLines());
    dispatch(setActiveBoxes([]));
  };

  return (
    <Section>
      <StyledTabs value={value} onChange={handleChange} variant="scrollable">
        <StyledTab label="General" />
        <StyledTab label="Customer" />
        <StyledTab label="Vendor" />
        <StyledTab label="Payment" />
        <StyledTab label="Items" />
      </StyledTabs>

      {value === 0 && (
        <TabContent>
          <TabHeader>
            <Extraction label="Field name" value="Value"></Extraction>
          </TabHeader>
          {extractionsGeneral.map(({ label, value, confidence, bbox_ids }) => (
            <Extraction
              key={label}
              label={label}
              value={value}
              confidence={confidence}
              onMouseOver={(e) => handleMouseOver(e, bbox_ids)}
              onMouseOut={handleMouseOut}
            />
          ))}
        </TabContent>
      )}

      {value === 1 && (
        <TabContent>
          <TabHeader>
            <Extraction label="Field name" value="Value"></Extraction>
          </TabHeader>
          {extractionsCustomer.map(({ label, value, confidence, bbox_ids }) => (
            <Extraction
              key={label}
              label={label}
              value={value}
              confidence={confidence}
              onMouseOver={(e) => handleMouseOver(e, bbox_ids)}
              onMouseOut={handleMouseOut}
            />
          ))}
        </TabContent>
      )}

      {value === 2 && (
        <TabContent>
          <TabHeader>
            <Extraction label="Field name" value="Value"></Extraction>
          </TabHeader>
          {extractionsVendor.map(({ label, value, confidence, bbox_ids }) => (
            <Extraction
              key={label}
              label={label}
              value={value}
              confidence={confidence}
              onMouseOver={(e) => handleMouseOver(e, bbox_ids)}
              onMouseOut={handleMouseOut}
            />
          ))}
        </TabContent>
      )}

      {value === 3 && (
        <TabContent>
          <TabHeader>
            <Extraction label="Field name" value="Value"></Extraction>
          </TabHeader>
          {extractionsPayment.map(({ label, value, confidence, bbox_ids }) => (
            <Extraction
              key={label}
              label={label}
              value={value}
              confidence={confidence}
              onMouseOver={(e) => handleMouseOver(e, bbox_ids)}
              onMouseOut={handleMouseOut}
            />
          ))}
        </TabContent>
      )}

      {value === 4 && (
        <TabContent>
          <TabHeader>
            <Extraction label="Field name" value="Value"></Extraction>
          </TabHeader>

          {extractionsItems.length > 1 && (
            <Flex justifyContent="center" pt={["15px", "35px"]} pb={[0]}>
              <Pagination
                variant="small"
                count={extractionsItems.length}
                currentIndex={currentLineItem}
                onClick={setCurrentLineItem}
              ></Pagination>
            </Flex>
          )}

          {extractionsItems.map((item, index) => (
            <LineItemGroup
              key={`line-item-${index}`}
              isVisible={currentLineItem === index}
            >
              {item.props.map(({ label, value, confidence, bbox_ids }) => (
                <Extraction
                  key={label}
                  label={label}
                  value={value}
                  confidence={confidence}
                  onMouseOver={(e) => handleMouseOver(e, bbox_ids)}
                  onMouseOut={handleMouseOut}
                />
              ))}
            </LineItemGroup>
          ))}
        </TabContent>
      )}
    </Section>
  );
};

export default CustomTabs;
